import { fist, palm, peak, slash, star, swirl, taiji } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * หมัดหมอกวน (ยอดหมอก, taiji): soft hands that turn force back. Slate-blue mist ribbons, ink peaks and the
 * taiji disc on the pale tile; borrowed force is drawn as a curve that bends round and returns.
 */

const MIST = '#8ea6c2';
const MIST_HI = '#dfe9f4';
const SLATE = '#4a5a74';
const INK = '#26283a';
const SKIN = C.skin;

/** An arrowhead at (x,y) pointing along (dx,dy) (unit steps). */
function head(p: Pix, x: number, y: number, dx: number, dy: number, col: string): void {
  p.poly([
    [x + dx * 3, y + dy * 3],
    [x - dy * 3, y + dx * 3],
    [x + dy * 3, y - dx * 3],
  ], col);
}

registerIcons('skill', {
  // Pushing force back: an incoming line bends round the palm and returns the way it came.
  'tf-push': (p) => {
    p.path([
      [2, 8],
      [14, 8],
    ], SLATE, 2);
    p.ring(14, 15, 7, 7, MIST, -Math.PI / 2, Math.PI / 2);
    p.path([
      [14, 22],
      [5, 22],
    ], MIST, 3);
    p.path([
      [14, 22],
      [6, 22],
    ], MIST_HI, 1);
    head(p, 4, 22, -1, 0, MIST_HI);
    palm(p, 18, 9, SKIN, { flip: true, lines: C.skinDark });
  },
  // A mist sleeve winds three times round the foe's arm and pulls the guard loose.
  'tf-grasp': (p) => {
    p.rect(13, 9, 6, 17, C.steelMid).rect(17, 9, 1, 17, C.steel);
    fist(p, 10, 1, SKIN);
    for (let k = 0; k < 3; k++) {
      const y = 11 + k * 5;
      p.thick(9, y + 3, 23, y, MIST, 2);
      p.line(9, y + 3, 23, y, MIST_HI);
    }
    p.path([
      [23, 21],
      [27, 24],
      [24, 28],
    ], MIST, 2);
    head(p, 4, 18, -1, 0, MIST_HI);
    p.rect(5, 18, 4, 1, MIST_HI);
  },
  // Willow arms whip out on both sides at once.
  'tf-whip': (p) => {
    slash(p, 16, 17, 13, Math.PI * 0.75, Math.PI * 1.35, MIST, 3);
    slash(p, 16, 17, 13, -Math.PI * 0.35, Math.PI * 0.25, MIST, 3);
    taiji(p, 16, 17, 5, MIST_HI, INK);
    for (const [x, y] of [
      [3, 26],
      [28, 26],
    ] as const)
      p.rect(x, y, 2, 1, MIST);
  },
  // Cloud hands: two palms circling a slow taiji, the turn drawn as a mist ring.
  'tf-clouds': (p) => {
    p.ring(17, 18, 11, 9, MIST, -Math.PI * 0.2, Math.PI * 1.15);
    head(p, 7, 14, 0, -1, MIST);
    taiji(p, 17, 18, 6, MIST_HI, INK);
    p.ell(26, 7, 2, 2, MIST_HI);
  },
  // Soft takes the mountain: a falling peak met by a bowl of mist and an open palm.
  'tf-yield': (p) => {
    peak(p, 18, 15, 16, 12, SLATE, MIST_HI);
    p.ring(18, 17, 11, 6, MIST, 0, Math.PI);
    p.ring(18, 17, 10, 5, MIST_HI, 0.2, Math.PI - 0.2);
    palm(p, 12, 19, SKIN, { flipY: false, lines: C.skinDark });
  },
  // One turning step through the mist into a palm.
  'tf-step': (p) => {
    p.ring(13, 17, 9, 8, MIST, Math.PI * 0.35, Math.PI * 1.75);
    for (const [x, y] of [
      [6, 23],
      [4, 16],
      [7, 10],
    ] as const)
      p.rect(x, y, 3, 2, SLATE);
    palm(p, 16, 7, SKIN, { lines: C.skinDark });
    p.rect(22, 24, 6, 1, MIST_HI).rect(20, 27, 6, 1, MIST);
  },
  // The yin-yang wheel turning, its rim cutting all round.
  'tf-wheel': (p) => {
    slash(p, 16, 16, 13, -Math.PI * 0.9, -Math.PI * 0.1, MIST, 3);
    slash(p, 16, 16, 13, Math.PI * 0.1, Math.PI * 0.9, MIST, 3);
    taiji(p, 16, 16, 9, MIST_HI, INK);
  },
  // The great mist vortex with the taiji at its eye; dazed sparks at its rim.
  'tf-supreme': (p) => {
    swirl(p, 16, 16, 13, MIST, { arms: 4, turn: 4.4 });
    taiji(p, 16, 16, 6, MIST_HI, INK);
    star(p, 25, 7, 2, MIST_HI);
    star(p, 7, 25, 2, MIST_HI);
  },
});
